'use client';

import React, { useRef, useState, useEffect } from 'react';

import type { LandscapeDisplay } from '@/types';
import { getMediaData, getFilmData } from '@/lib/utils/media.utils';
import VolumeControl from './ui/volume';
import VideoProgress from './ui/progress';

interface LandscapeDisplayProps {
  layout: LandscapeDisplay;
}

let scrollContainer: HTMLElement | null = null;

export const Landscape_Display: React.FC<LandscapeDisplayProps> = ({ layout }) => {
  const bgImage = getMediaData(layout.image);
  const videoData = getFilmData(layout.video);
  const videoRef = useRef<HTMLVideoElement>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);

  // Video süresini ve mevcut zamanı tutmak için state ekleyelim
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  // Scroll performansı için rafID ve last scroll position
  const rafIDRef = useRef<number | null>(null);
  const lastScrollTopRef = useRef<number>(0);
  const ticking = useRef<boolean>(false);

  // Scroll efekti için useEffect hook'u - sadece video yoksa çalışacak
  useEffect(() => {
    // Video varsa parallax efektini uygulamayalım
    if (videoData.url) return undefined;

    if (!scrollContainer) {
      scrollContainer = document.getElementById('landscape-content');
    }
    if (!scrollContainer || !parallaxRef.current) return undefined;

    // Throttle kullanarak scroll işlemlerini optimize edelim
    const handleScroll = () => {
      const scrollTop = scrollContainer.scrollTop;

      // Sadece scroll değeri değişirse ve ticking false ise işlem yapalım
      if (lastScrollTopRef.current !== scrollTop && !ticking.current) {
        ticking.current = true;

        // requestAnimationFrame ile performans optimize edilir
        rafIDRef.current = window.requestAnimationFrame(() => {
          if (parallaxRef.current) {
            // 0.5 çarpanı ile daha yumuşak bir parallax efekti
            const translateY = (scrollTop - 250) * 0.5;
            // GPU ivmelenmesi için translate3d kullanıyoruz
            parallaxRef.current.style.transform = `translate3d(0, ${translateY}px, 0)`;
          }

          lastScrollTopRef.current = scrollTop;
          ticking.current = false;
        });
      }
    };

    // Scroll eventi ekleyelim
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      // Temizleme işlemi
      scrollContainer.removeEventListener('scroll', handleScroll);

      // Animation frame'i iptal edelim
      if (rafIDRef.current) {
        window.cancelAnimationFrame(rafIDRef.current);
      }
    };
  }, [videoData.url]);

  return (
    <section className="w-full relative overflow-hidden mb-[12px] rounded-md">
      {/* Parallax Container - Video varsa sabit pozisyonda kalmayacak */}
      <div
        ref={parallaxRef}
        className={`${videoData.url ? '' : 'sticky'} top-0 left-0 w-full`}
        style={{
          height: videoData.url ? 'auto' : '500px',
          zIndex: 1,
          willChange: videoData.url ? 'auto' : 'transform', // Video yoksa GPU kullanımını optimize eder
        }}
      >
        <div
          className={`${videoData.url ? '' : 'p-[15px] mb-[12px]'} rounded-md flex
     flex-col justify-between relative overflow-hidden`}
          style={{
            height: videoData.url ? 'auto' : '180%',
            // A film is shown by itself, edge to edge. Its still is the film's own poster, so
            // drawing the still behind it as well showed the same picture twice, one a frame
            // around the other.
            background: videoData.url
              ? layout.bgColor || '#000'
              : (bgImage.url ? `url('${bgImage.url}')` : ' ') + (layout.bgColor ? layout.bgColor : 'white'),
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            backfaceVisibility: 'hidden', // Render performansı iyileştirir
          }}
        >
          {videoData.url && (
            <VideoProgress
              className="bottom-4 center-4 z-[100] left-[50%] -translate-x-1/2"
              initialProgress={currentTime}
              duration={videoDuration}
              onChange={progress => {
                if (videoRef.current) {
                  videoRef.current.currentTime = progress;
                }
              }}
              onPlayToggle={() => {
                if (videoRef.current) {
                  if (videoRef.current.paused) {
                    videoRef.current.play();
                    // setIsPlaying(true);
                  } else {
                    videoRef.current.pause();
                    // setIsPlaying(false);
                  }
                }
              }}
            />
          )}
          {videoData.url && (
            <VolumeControl
              onChange={volume => {
                if (videoRef.current) {
                  videoRef.current.volume = volume / 100;
                  videoRef.current.muted = volume === 0;
                }
              }}
            />
          )}
          {videoData.url && (
            <video
              ref={videoRef}
              src={videoData.url}
              autoPlay
              muted
              loop
              playsInline
              poster={bgImage.url}
              onLoadedMetadata={() => {
                if (videoRef.current) {
                  setVideoDuration(videoRef.current.duration);
                }
              }}
              onTimeUpdate={() => {
                if (videoRef.current) {
                  setCurrentTime(videoRef.current.currentTime);
                }
              }}
              onDoubleClick={() => {
                if (videoRef.current) {
                  if (videoRef.current.requestFullscreen) {
                    videoRef.current.requestFullscreen();
                  }
                }
              }}
              // onClick={() => {
              //   if (videoRef.current) {
              //     if (isPlaying) {
              //       videoRef.current.pause();
              //     } else {
              //       videoRef.current.play();
              //     }
              //     setIsPlaying(!isPlaying);
              //   }
              // }}
              style={{
                width: '100%',
                height: 'auto', // Aspect ratio korunsun
                objectFit: 'contain',
                position: 'relative', // Absolute yerine relative kullanarak doğal boyutunu almasını sağlıyoruz
                zIndex: 10,
              }}
            />
          )}
        </div>
      </div>

      {/* İsterseniz buraya scroll içeriğinin devamını ekleyin */}
    </section>
  );
};
